import { match } from "ts-pattern";
import { z } from "zod";
import type { CabinetState } from "../../../context/StoreContext";
import {
	type ApiData,
	type ObjectList,
	getCurrentThickness,
	isLongPartsBuildMethod,
} from "../data";
import { BackgroundSchema, createBackground } from "./Background";
import { DoorSchema, createDoor } from "./Door";
import { DoubleDoorSchema, MAX_DOUBLE_DOOR_WIDTH, createDoubleDoor } from "./DoubleDoor";
import { ShelfSchema, createShelves } from "./Shelf";

export type VerticalSide = "left" | "right";
export type HorizontalSide = "top" | "bottom";

export type Side = VerticalSide | HorizontalSide;

export enum CompartmentPosition {
	First = 0,
	Last = 1,
	FirstAndLast = 2,
	Surrounded = 3,
}

const DoorTypeSchema = z.discriminatedUnion("type", [DoorSchema, DoubleDoorSchema]).nullable();

export const CompartmentSchema = z.object({
	id: z.string(),
	x: z.number(),
	y: z.number(),
	z: z.number(),
	width: z.number(),
	height: z.number(),
	depth: z.number(),

	door: DoorTypeSchema,
	shelves: z.array(ShelfSchema),
	background: z.optional(BackgroundSchema),
});

export type DoorType = z.infer<typeof DoorTypeSchema>;

export type CompartmentType = z.infer<typeof CompartmentSchema>;

export function updateCompartment(input: CompartmentType, thickness: number): CompartmentType {
	return {
		...input,
		x: Math.round(input.x),
		y: Math.round(input.y),
		z: Math.round(input.z),
		width: Math.round(input.width),
		height: Math.round(input.height),
		depth: Math.round(input.depth),
		door: match(input.door)
			.with({ type: "single", side: "left" }, () => createDoor(input, "left"))
			.with({ type: "single", side: "right" }, () => createDoor(input, "right"))
			.with({ type: "double" }, () => createDoubleDoor(input))
			.otherwise(() => null),
		background: createBackground(input),
		shelves: createShelves(input, input.shelves.length, thickness),
	};
}

export function getLeftCompartments(
	compartment: CompartmentType,
	compartments: CompartmentType[],
	thickness: number,
): CompartmentType[] {
	return compartments
		.filter((c) => compartmentsOnSameY(compartment, c))
		.filter((c) => c.x + c.width + thickness === compartment.x);
}

function getRightCompartments(
	compartment: CompartmentType,
	compartments: CompartmentType[],
	thickness: number,
): CompartmentType[] {
	return compartments
		.filter((c) => compartmentsOnSameY(compartment, c))
		.filter((c) => c.x === compartment.x + compartment.width + thickness);
}

function getTopCompartments(
	compartment: CompartmentType,
	compartments: CompartmentType[],
	thickness: number,
): CompartmentType[] {
	return compartments
		.filter((c) => compartmentsOnSameX(compartment, c))
		.filter((c) => c.y === compartment.y + compartment.height + thickness);
}

function getBottomCompartments(
	compartment: CompartmentType,
	compartments: CompartmentType[],
	thickness: number,
): CompartmentType[] {
	return compartments
		.filter((c) => compartmentsOnSameX(compartment, c))
		.filter((c) => c.y + c.height + thickness === compartment.y);
}

export function getSurroundingCompartments(
	compartment: CompartmentType,
	compartments: CompartmentType[],
	thickness: number,
) {
	return {
		left: getLeftCompartments(compartment, compartments, thickness),
		right: getRightCompartments(compartment, compartments, thickness),
		top: getTopCompartments(compartment, compartments, thickness),
		bottom: getBottomCompartments(compartment, compartments, thickness),
	} as const;
}

export function getNewXValue(
	compartment: CompartmentType,
	side: VerticalSide,
	diff: number,
	state: CabinetState,
	api?: ApiData,
) {
	const startPoint =
		side === "left" ? compartment.x : compartment.x + compartment.width + state.thickness;

	const snap = getSnappingPointsOnXAxis(compartment, state, startPoint + diff, side, api);

	return Math.round(snap - startPoint);
}

export function getNewYValue(
	compartment: CompartmentType,
	side: HorizontalSide,
	diff: number,
	state: CabinetState,
	api?: ApiData,
) {
	const startPoint =
		side === "bottom" ? compartment.y : compartment.y + compartment.height + state.thickness;

	const snap = getSnappingPointsOnYAxis(
		compartment,
		state.compartments,
		state,
		startPoint + diff,
		side,
		api,
	);

	return Math.round(snap - startPoint);
}

export function compartmentsOnSameX(a: CompartmentType, b: CompartmentType): boolean {
	return (b.x >= a.x && b.x <= a.x + a.width) || (a.x >= b.x && a.x <= b.x + b.width);
}

export function compartmentsOnSameY(a: CompartmentType, b: CompartmentType): boolean {
	return (b.y >= a.y && b.y <= a.y + a.height) || (b.y <= a.y && b.y + b.height >= a.y);
}

export function getRightCompartmentsRecursively(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	rightCompartments: ObjectList<CompartmentType> = new Map(),
): ObjectList<CompartmentType> {
	const array = Array.from(compartments.values());
	const right = getRightCompartments(compartment, array, thickness);
	const currentSize = rightCompartments.size;

	for (const t of right) {
		rightCompartments.set(t.id, t);
	}

	if (currentSize === rightCompartments.size) {
		return rightCompartments;
	}

	for (const r of right) {
		const lefts = getLeftCompartments(r, array, thickness);

		for (const l of lefts) {
			getRightCompartmentsRecursively(l, compartments, thickness, rightCompartments);
		}
	}

	return rightCompartments;
}

export function getLeftCompartmentsRecursively(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	leftCompartments: ObjectList<CompartmentType> = new Map(),
): ObjectList<CompartmentType> {
	const array = Array.from(compartments.values());
	const lefts = getLeftCompartments(compartment, array, thickness);
	const currentSize = leftCompartments.size;

	for (const l of lefts) {
		leftCompartments.set(l.id, l);
	}

	if (currentSize === leftCompartments.size) {
		return leftCompartments;
	}

	for (const left of lefts) {
		const rights = getRightCompartments(left, array, thickness);

		for (const right of rights) {
			getLeftCompartmentsRecursively(right, compartments, thickness, leftCompartments);
		}
	}

	return leftCompartments;
}

export function getTopCompartmentsRecursively(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	topCompartments: ObjectList<CompartmentType> = new Map(),
): ObjectList<CompartmentType> {
	const array = Array.from(compartments.values());
	const tops = getTopCompartments(compartment, array, thickness);
	const currentSize = topCompartments.size;

	for (const t of tops) {
		topCompartments.set(t.id, t);
	}

	if (currentSize === topCompartments.size) {
		return topCompartments;
	}

	for (const top of tops) {
		const bottoms = getBottomCompartments(top, array, thickness);

		for (const bottom of bottoms) {
			getTopCompartmentsRecursively(bottom, compartments, thickness, topCompartments);
		}
	}

	return topCompartments;
}

export function getBottomCompartmentsRecursively(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	bottomCompartments: ObjectList<CompartmentType> = new Map(),
): ObjectList<CompartmentType> {
	const array = Array.from(compartments.values());
	const bottoms = getBottomCompartments(compartment, array, thickness);
	const currentSize = bottomCompartments.size;

	for (const b of bottoms) {
		bottomCompartments.set(b.id, b);
	}

	if (currentSize === bottomCompartments.size) {
		return bottomCompartments;
	}

	for (const bottom of bottoms) {
		const tops = getTopCompartments(bottom, array, thickness);

		for (const top of tops) {
			getBottomCompartmentsRecursively(top, compartments, thickness, bottomCompartments);
		}
	}

	return bottomCompartments;
}

export function getCombinedHeight(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	side: VerticalSide,
): number {
	const nextCompartments =
		side === "left"
			? getLeftCompartmentsRecursively(compartment, compartments, thickness)
			: getRightCompartmentsRecursively(compartment, compartments, thickness);

	const height = [...nextCompartments.values()]
		.map((c) => c.height)
		.reduce((carry, value) => carry + value, 0);

	const combinedHeight = height + thickness * (nextCompartments.size - 1);

	return combinedHeight;
}

export function getCombinedLength(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	side: HorizontalSide,
): number {
	const nextCompartments =
		side === "top"
			? getTopCompartmentsRecursively(compartment, compartments, thickness)
			: getBottomCompartmentsRecursively(compartment, compartments, thickness);

	const length = [...nextCompartments.values()]
		.map((c) => c.width)
		.reduce((carry, value) => carry + value, 0);

	const combinedLength = length + thickness * (nextCompartments.size - 1);

	return combinedLength;
}

export function getCombinedX(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	side: HorizontalSide,
) {
	const nextCompartments =
		side === "top"
			? getTopCompartmentsRecursively(compartment, compartments, thickness)
			: getBottomCompartmentsRecursively(compartment, compartments, thickness);

	const x = [...nextCompartments.values()].map((c) => c.x);

	if (x.length === 0) {
		if (compartment.y !== thickness) {
			console.warn(
				`Compartment {x: ${compartment.x}, y: ${compartment.y}} has no neighbors in the ${side} direction`,
			);
		}

		return 0;
	}

	return Math.min(...x);
}

export function getCombinedY(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	side: VerticalSide,
) {
	const nextCompartments =
		side === "left"
			? getLeftCompartmentsRecursively(compartment, compartments, thickness)
			: getRightCompartmentsRecursively(compartment, compartments, thickness);

	const y = [...nextCompartments.values()].map((c) => c.y);

	if (y.length === 0) {
		if (compartment.x !== thickness) {
			console.warn(
				`Compartment {x: ${compartment.x}, y: ${compartment.y}} has no neighbors in the ${side} direction`,
			);
		}

		return 0;
	}

	return Math.min(...y);
}

export function isNextToCompartmentOnX(
	a: CompartmentType,
	b: CompartmentType,
	thickness: number,
): boolean {
	return a.x === b.x + b.width + thickness || a.x + a.width + thickness === b.x;
}

export function isNextToCompartmentOnY(
	a: CompartmentType,
	b: CompartmentType,
	thickness: number,
): boolean {
	return a.y === b.y + b.height + thickness || a.y + a.height + thickness === b.y;
}

function getSnappingPointsOnYAxis(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	state: CabinetState,
	y: number,
	side: HorizontalSide,
	api?: ApiData,
): number {
	const thickness = state.thickness;
	const combinedX = getCombinedX(compartment, compartments, thickness, side);
	const combinedLength = getCombinedLength(compartment, compartments, thickness, side);

	const points = Array.from(compartments.values())
		.filter(
			(c) =>
				combinedX + combinedLength + thickness === c.x ||
				compartment.x === c.x + c.width + thickness,
		)
		.flatMap((c) => [c.y, c.y + c.height + thickness]);

	const uniquePoints = [...new Set(points)];

	return getClosestSnappingPoint(uniquePoints, y, state, api);
}

function getSnappingPointsOnXAxis(
	compartment: CompartmentType,
	state: CabinetState,
	x: number,
	side: VerticalSide,
	api?: ApiData,
): number {
	const thickness = state.thickness;
	const combinedY = getCombinedY(compartment, state.compartments, thickness, side);
	const combinedHeight = getCombinedHeight(compartment, state.compartments, thickness, side);

	const points = Array.from(state.verticals.values())
		.filter(
			(v) =>
				v.y === combinedY + combinedHeight + thickness ||
				v.y + v.width === compartment.y - thickness,
		)
		.flatMap((v) => [v.x + thickness])
		.sort((a, b) => a - b);

	const uniquePoints = [...new Set(points)];

	return getClosestSnappingPoint(uniquePoints, x, state, api);
}

export function getClosestSnappingPoint(
	snappingPoints: number[],
	goal: number,
	state: CabinetState,
	api?: ApiData,
): number {
	if (snappingPoints.length) {
		const point = snappingPoints.reduce((prev, curr) =>
			Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev,
		);
		const margin = getCurrentThickness(state, api)?.parts.vertical.constraints.width.min ?? 50;

		return goal >= point - margin && goal <= point + margin ? point : goal;
	}

	return goal;
}

export function getAllSnappingPointsOnXAxis(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	state: CabinetState,
	x: number,
	api?: ApiData,
) {
	const thickness = state.thickness;
	const points = Array.from(compartments.values())
		.filter(
			(c) =>
				compartment.y + compartment.height + thickness === c.y ||
				compartment.y === c.y + c.height + thickness,
		)
		.flatMap((c) => [c.x, c.x + c.width + thickness]);

	const uniquePoints = [...new Set(points)];

	return getClosestSnappingPoint(uniquePoints, x, state, api);
}

export function getAllSnappingPointsOnYAxis(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	state: CabinetState,
	x: number,
	api?: ApiData,
) {
	const thickness = state.thickness;
	const points = Array.from(compartments.values())
		.filter(
			(c) =>
				compartment.x + compartment.width + thickness === c.x ||
				compartment.x === c.x + c.width + thickness,
		)
		.flatMap((c) => [c.y, c.y + c.height + thickness]);

	const uniquePoints = [...new Set(points)];

	return getClosestSnappingPoint(uniquePoints, x, state, api);
}

export function getCompartmentsOnSide(
	compartment: CompartmentType,
	side: Side,
	state: CabinetState,
) {
	const method = match(side)
		.with("top", () => getTopCompartmentsRecursively)
		.with("bottom", () => getBottomCompartmentsRecursively)
		.with("left", () => getLeftCompartmentsRecursively)
		.with("right", () => getRightCompartmentsRecursively)
		.exhaustive();

	return method(compartment, state.compartments, state.thickness);
}

export function canMergeWith(
	compartment: CompartmentType,
	side: Side,
	state: CabinetState,
	api?: ApiData,
): CompartmentType | null {
	const availableCompartments = getCompartmentsOnSide(compartment, side, state);
	// we can only merge when there is exactly one neighboring compartment
	if (availableCompartments.size === 1) {
		// for some reason typescript doesn't understand the value from the map
		// so we need an explicit `as`
		const merge = availableCompartments.values().next().value as typeof compartment;

		const maxSize = getMaxSize(compartment, side, state, api);

		// check if the neighboring compartment is the same size as the current
		// compartment to prevent having to merge more than 2 compartments into 1
		return match(side)
			.with("left", "right", () => {
				const isSameHeight = merge.height === compartment.height;
				const inConstraints = compartment.width + merge.width + state.thickness <= maxSize;

				return isSameHeight && inConstraints ? merge : null;
			})
			.with("top", "bottom", () => {
				const isSameWidth = merge.width === compartment.width;
				const inConstraints = compartment.height + merge.height + state.thickness <= maxSize;

				return isSameWidth && inConstraints ? merge : null;
			})
			.exhaustive();
	}

	return null;
}

export function touchesEdges(
	comparment: CompartmentType,
	state: CabinetState,
): Record<Side, boolean> {
	return {
		left: comparment.x - state.thickness === 0,
		right: comparment.x + comparment.width + state.thickness === state.width,
		top: comparment.y + comparment.height === state.height,
		bottom: comparment.y - state.thickness === 0,
	};
}

export function getMaxSize(
	compartment: CompartmentType,
	side: Side,
	state: CabinetState,
	api?: ApiData,
) {
	const thickness = getCurrentThickness(state, api);

	const MAX_HEIGHT = thickness?.parts.background.constraints.width.max || 0;
	const MAX_WIDTH = thickness?.parts.background.constraints.depth.max || 0;

	return match(side)
		.with("top", "bottom", () => {
			const maxDoorHeight = thickness?.parts.door.constraints.depth.max ?? 0;
			const max = isLongPartsBuildMethod(state.thickness)
				? MAX_WIDTH
				: compartment.width > MAX_HEIGHT
					? MAX_HEIGHT
					: MAX_WIDTH;

			return match(compartment.door)
				.with({ type: "single" }, () => Math.min(max, maxDoorHeight))
				.with({ type: "double" }, () => Math.min(max, maxDoorHeight))
				.otherwise(() => max);
		})
		.with("left", "right", () => {
			const maxDoorWidth = thickness?.parts.door.constraints.width.max ?? 0;
			const max = isLongPartsBuildMethod(state.thickness)
				? MAX_HEIGHT
				: compartment.height > MAX_HEIGHT
					? MAX_HEIGHT
					: MAX_WIDTH;

			return match(compartment.door)
				.with({ type: "single" }, () => Math.min(max, maxDoorWidth))
				.with({ type: "double" }, () => Math.min(max, MAX_DOUBLE_DOOR_WIDTH))
				.otherwise(() => max);
		})
		.exhaustive();
}

export function getMinSize(
	compartment: CompartmentType,
	side: Side,
	state: CabinetState,
	api?: ApiData,
) {
	const thickness = getCurrentThickness(state, api);

	const min = thickness?.parts.background.constraints.width.min || 0;

	return match(side)
		.with("top", "bottom", () => {
			const minDoorHeight = thickness?.parts.door.constraints.depth.min ?? 0;

			return match(compartment.door)
				.with({ type: "single" }, () => Math.max(min, Math.max(compartment.width, minDoorHeight)))
				.with({ type: "double" }, () =>
					Math.max(min, Math.max(compartment.width / 2, minDoorHeight)),
				)
				.otherwise(() => min);
		})
		.with("left", "right", () => {
			const minDoorWidth = thickness?.parts.door.constraints.width.min ?? 0;

			return match(compartment.door)
				.with({ type: "single" }, () => Math.max(min, Math.min(compartment.height, minDoorWidth)))
				.with({ type: "double" }, () =>
					Math.min(min, Math.max(compartment.height / 2, minDoorWidth * 2)),
				)
				.otherwise(() => min);
		})
		.exhaustive();
}
